import {
  AFTER_MIDNIGHT_THRESHOLD,
  MIN_ASSIGNMENT_MINUTES,
  normalizeTimelineHour,
} from "@produce/shared";

/**
 * Pure time math for resizing an assignment's edge. Extracted from
 * CrewSchedule's handleResizeMove so it can be unit-tested and so all
 * comparisons run in *timeline minutes* — after-midnight hours (before the
 * 06:00 threshold) roll into the 24h+ range. Comparing raw clock minutes
 * instead made a set like 23:00→01:00 impossible to resize (01:00 read as
 * 60 min, "before" the 23:00 start).
 */

/** Minutes on the continuous timeline axis (after-midnight hours rolled +24h). */
export function toTimelineMinutes(time: string, fallbackHour = 0): number {
  const [, m] = time.split(":").map(Number);
  const hour = normalizeTimelineHour(time, AFTER_MIDNIGHT_THRESHOLD) ?? fallbackHour;
  return hour * 60 + (Number.isFinite(m) ? m : 0);
}

/** Timeline minutes back to a wall-clock "HH:MM" (wrapped to 24h). */
export function timelineMinutesToClock(minutes: number): string {
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export interface ResizeBounds {
  setStart: string;
  setEnd: string;
}

/**
 * Resolve the new clock time for the edge being dragged, or null when the
 * move is invalid (shorter than 15 min, or the dragged edge would cross the
 * opposite one). `bounds`, when given (artist-type assignments), clamps the
 * edge inside the artist's set window.
 */
export function resolveResizeTime(params: {
  targetTime: string;
  edge: "start" | "end";
  oppositeTime: string;
  bounds?: ResizeBounds | null;
  fallbackHour?: number;
}): string | null {
  const { targetTime, edge, oppositeTime, bounds, fallbackHour = 0 } = params;
  let minutes = toTimelineMinutes(targetTime, fallbackHour);

  if (bounds) {
    const setStart = toTimelineMinutes(bounds.setStart, fallbackHour);
    const setEnd = toTimelineMinutes(bounds.setEnd, fallbackHour);
    minutes = edge === "start" ? Math.max(minutes, setStart) : Math.min(minutes, setEnd);
  }

  const opposite = toTimelineMinutes(oppositeTime, fallbackHour);
  if (edge === "start") {
    if (minutes > opposite - MIN_ASSIGNMENT_MINUTES) return null;
  } else if (minutes < opposite + MIN_ASSIGNMENT_MINUTES) {
    return null;
  }
  return timelineMinutesToClock(minutes);
}
